import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { apiClient } from "../App";
import DashboardLayout from "../components/DashboardLayout";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Switch } from "../components/ui/switch";
import { toast } from "sonner";
import { 
  ArrowLeft, Plus, Package, Globe, Mail, Palette, 
  Edit2, Trash2, Copy, ExternalLink, Ticket
} from "lucide-react";

export default function ProductManagement() {
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    slug: "",
    domain: "",
    support_email: "",
    description: "",
    logo_url: "",
    primary_color: "#1a365d",
    is_active: true,
  });

  const fetchProducts = useCallback(async () => {
    try {
      const res = await apiClient.get("/products");
      setProducts(res.data);
    } catch (error) {
      toast.error("Failed to load products");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  const generateSlug = (name) => {
    return name.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");
  };

  const handleNameChange = (name) => {
    setFormData({
      ...formData,
      name,
      slug: generateSlug(name),
    });
  };

  const handleDomainChange = (domain) => {
    const cleanDomain = domain.toLowerCase().replace(/^https?:\/\//, "").replace(/\/$/, "");
    setFormData({
      ...formData,
      domain: cleanDomain,
      support_email: `support@${cleanDomain}`,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingProduct) {
        await apiClient.patch(`/products/${editingProduct.id}`, {
          name: formData.name,
          description: formData.description,
          logo_url: formData.logo_url,
          primary_color: formData.primary_color,
          is_active: formData.is_active,
        });
        toast.success("Product updated successfully");
      } else {
        await apiClient.post("/products", formData);
        toast.success("Product created successfully");
      }
      setCreateDialogOpen(false);
      setEditingProduct(null);
      setFormData({
        name: "",
        slug: "",
        domain: "",
        support_email: "",
        description: "",
        logo_url: "",
        primary_color: "#1a365d",
        is_active: true,
      });
      fetchProducts();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save product");
    }
  };

  const handleEdit = (product) => {
    setEditingProduct(product);
    setFormData({
      name: product.name,
      slug: product.slug,
      domain: product.domain,
      support_email: product.support_email,
      description: product.description || "",
      logo_url: product.logo_url || "",
      primary_color: product.primary_color || "#1a365d",
      is_active: product.is_active,
    });
    setCreateDialogOpen(true);
  };

  const handleDelete = async (productId) => {
    if (!window.confirm("Are you sure you want to delete this product?")) return;
    
    try {
      await apiClient.delete(`/products/${productId}`);
      toast.success("Product deleted");
      fetchProducts();
    } catch (error) {
      toast.error("Failed to delete product");
    }
  };

  const copyWebhookUrl = (product) => {
    const webhookUrl = `https://helpdesk.sebasalliance.com/api/webhooks/email/inbound`;
    navigator.clipboard.writeText(webhookUrl);
    toast.success("Webhook URL copied! Configure this in SendGrid Inbound Parse.");
  };

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="product-management-page">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate("/admin")}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div className="flex-1">
            <h1 className="font-heading text-2xl font-bold text-slate-900">Product Management</h1>
            <p className="text-slate-500 mt-1">Manage SAG digital products and support channels</p>
          </div>
          <Dialog open={createDialogOpen} onOpenChange={(open) => {
            setCreateDialogOpen(open);
            if (!open) {
              setEditingProduct(null);
              setFormData({
                name: "",
                slug: "",
                domain: "",
                support_email: "",
                description: "",
                logo_url: "",
                primary_color: "#1a365d",
                is_active: true,
              });
            }
          }}>
            <DialogTrigger asChild>
              <Button className="bg-sag-navy hover:bg-sag-navy-800" data-testid="add-product-button">
                <Plus className="h-4 w-4 mr-2" />
                Add Product
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-[550px]">
              <DialogHeader>
                <DialogTitle className="font-heading text-xl">
                  {editingProduct ? "Edit Product" : "Add New Product"}
                </DialogTitle>
                <DialogDescription>
                  {editingProduct 
                    ? "Update product details and branding"
                    : "Add a new SAG digital product to the helpdesk"
                  }
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Product Name</Label>
                  <Input
                    id="name"
                    placeholder="Caribbean Payments Alliance"
                    value={formData.name}
                    onChange={(e) => handleNameChange(e.target.value)}
                    required
                    data-testid="product-name-input"
                  />
                </div>
                
                {!editingProduct && (
                  <>
                    <div className="space-y-2">
                      <Label htmlFor="domain">Domain</Label>
                      <Input
                        id="domain"
                        placeholder="caribbeanpaymentsalliance.com"
                        value={formData.domain}
                        onChange={(e) => handleDomainChange(e.target.value)}
                        required
                        data-testid="product-domain-input"
                      />
                      <p className="text-xs text-slate-500">
                        Enter the domain without http:// or https://
                      </p>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="slug">Slug</Label>
                        <Input
                          id="slug"
                          value={formData.slug}
                          onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
                          required
                          data-testid="product-slug-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="support_email">Support Email</Label>
                        <Input
                          id="support_email"
                          value={formData.support_email}
                          onChange={(e) => setFormData({ ...formData, support_email: e.target.value })}
                          required
                          data-testid="product-email-input"
                        />
                      </div>
                    </div>
                  </>
                )}
                
                <div className="space-y-2">
                  <Label htmlFor="description">Description (Optional)</Label>
                  <Textarea
                    id="description"
                    placeholder="Brief description of this product..."
                    rows={2}
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    data-testid="product-description-input"
                  />
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="logo_url">Logo URL (Optional)</Label>
                    <Input
                      id="logo_url"
                      placeholder="https://..."
                      value={formData.logo_url}
                      onChange={(e) => setFormData({ ...formData, logo_url: e.target.value })}
                      data-testid="product-logo-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="primary_color">Brand Color</Label>
                    <div className="flex gap-2">
                      <Input
                        id="primary_color"
                        type="color"
                        value={formData.primary_color}
                        onChange={(e) => setFormData({ ...formData, primary_color: e.target.value })}
                        className="w-12 h-10 p-1 cursor-pointer"
                        data-testid="product-color-input"
                      />
                      <Input
                        value={formData.primary_color}
                        onChange={(e) => setFormData({ ...formData, primary_color: e.target.value })}
                        className="flex-1"
                      />
                    </div>
                  </div>
                </div>
                
                <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                  <div>
                    <Label>Active Status</Label>
                    <p className="text-xs text-slate-500">Allow tickets for this product</p>
                  </div>
                  <Switch
                    checked={formData.is_active}
                    onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
                    data-testid="product-active-switch"
                  />
                </div>
                
                <div className="flex justify-end gap-3 pt-4">
                  <Button type="button" variant="outline" onClick={() => setCreateDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button type="submit" className="bg-sag-navy hover:bg-sag-navy-800" data-testid="save-product-button">
                    {editingProduct ? "Update Product" : "Add Product"}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* SendGrid Setup Info */}
        <Card className="bg-blue-50 border-blue-200">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <Mail className="h-5 w-5 text-blue-600 mt-0.5" />
              <div>
                <h3 className="font-semibold text-blue-900">Email-to-Ticket Setup</h3>
                <p className="text-sm text-blue-700 mt-1">
                  To enable automatic ticket creation from emails, configure SendGrid Inbound Parse 
                  for each product domain. Use the webhook URL below and set up MX records as per 
                  SendGrid documentation.
                </p>
                <div className="mt-2 flex items-center gap-2">
                  <code className="text-xs bg-blue-100 px-2 py-1 rounded">
                    https://helpdesk.sebasalliance.com/api/webhooks/email/inbound
                  </code>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={() => {
                      navigator.clipboard.writeText("https://helpdesk.sebasalliance.com/api/webhooks/email/inbound");
                      toast.success("Webhook URL copied!");
                    }}
                  >
                    <Copy className="h-3 w-3" />
                  </Button>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Products Table */}
        <Card className="sag-card">
          <CardHeader className="border-b border-slate-100">
            <CardTitle className="font-heading text-lg">Registered Products</CardTitle>
            <CardDescription>Digital products under Sebastian Alliance Group</CardDescription>
          </CardHeader>
          <CardContent className="p-0">
            {loading ? (
              <div className="p-8 text-center text-slate-500">Loading products...</div>
            ) : products.length === 0 ? (
              <div className="p-8 text-center">
                <Package className="h-12 w-12 mx-auto text-slate-300 mb-4" />
                <p className="text-slate-500">No products registered yet</p>
                <p className="text-sm text-slate-400 mt-1">Add your first product to start receiving support tickets</p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Product</TableHead>
                    <TableHead>Domain</TableHead>
                    <TableHead>Support Email</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {products.map((product) => (
                    <TableRow key={product.id} data-testid={`product-row-${product.id}`}>
                      <TableCell>
                        <div className="flex items-center gap-3">
                          {product.logo_url ? (
                            <img 
                              src={product.logo_url} 
                              alt={product.name} 
                              className="w-10 h-10 rounded-lg object-contain bg-slate-100"
                            />
                          ) : (
                            <div 
                              className="w-10 h-10 rounded-lg flex items-center justify-center text-white font-bold"
                              style={{ backgroundColor: product.primary_color }}
                            >
                              {product.name.charAt(0)}
                            </div>
                          )}
                          <div>
                            <p className="font-medium text-slate-900">{product.name}</p>
                            <p className="text-xs text-slate-500">{product.slug}</p>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1 text-slate-600">
                          <Globe className="h-3 w-3" />
                          {product.domain}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1 text-slate-600">
                          <Mail className="h-3 w-3" />
                          {product.support_email}
                        </div>
                      </TableCell>
                      <TableCell>
                        {product.is_active ? (
                          <Badge className="bg-emerald-100 text-emerald-700" variant="secondary">
                            Active
                          </Badge>
                        ) : (
                          <Badge className="bg-slate-100 text-slate-600" variant="secondary">
                            Inactive
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleEdit(product)}
                            data-testid={`edit-product-${product.id}`}
                          >
                            <Edit2 className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDelete(product.id)}
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            data-testid={`delete-product-${product.id}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
